import type { Auth, User, UserInfo } from 'firebase/auth';
import type { AppUser } from '../types';

export type ProfileSyncResult = {
  synced?: boolean;
  needsProvisioning?: boolean;
  needsLinking?: boolean;
  message?: string;
  role?: string;
  houseId?: string;
};

export async function syncProvisionedProfile(): Promise<ProfileSyncResult> {
  const { syncRegisteredProfile } = await import('./firebase/FunctionsService.js');
  return syncRegisteredProfile() as Promise<ProfileSyncResult>;
}

export async function signOutUnprovisionedAuth(): Promise<void> {
  const { signOutUser } = await import('./FirebaseAuthService.js');
  await signOutUser();
}

type MapFirebaseUserFn = (user: User) => Promise<AppUser>;

export async function syncProfileAfterSignIn(
  mapFirebaseUser: MapFirebaseUserFn,
  auth: Auth,
): Promise<AppUser | null> {
  let syncResult: ProfileSyncResult = { synced: false };
  try {
    syncResult = await syncProvisionedProfile();
  } catch (err) {
    console.warn('[Auth] Profile sync failed:', err);
  }

  // Register this login device for trust tracking
  try {
    const { registerLoginDeviceOnSignIn } = await import('./deviceTrust.js');
    await registerLoginDeviceOnSignIn();
  } catch (err) {
    console.warn('[Auth] Device registration failed:', err);
  }

  if (syncResult.needsProvisioning) {
    await signOutUnprovisionedAuth();
    throw Object.assign(
      new Error('Account is not provisioned. Contact your banker or administrator.'),
      { code: 'auth/not-provisioned' },
    );
  }

  if (syncResult.needsLinking) {
    await signOutUnprovisionedAuth();
    throw Object.assign(
      new Error(syncResult.message ?? 'Sign in with your password first to link Google.'),
      { code: 'auth/needs-linking' },
    );
  }

  const user = await refreshAndMapUser(mapFirebaseUser, auth);
  if (!user) return null;

  try {
    const { assertDeviceTrustedOnLogin } = await import('./deviceTrustLogin.js');
    await assertDeviceTrustedOnLogin(user.role);
  } catch (err) {
    const code = String((err as { code?: string })?.code ?? '');
    if (code === 'auth/device-blocked' || code === 'auth/device-pending') {
      await signOutUnprovisionedAuth();
    }
    if (code === 'auth/device-passkey' || code === 'auth/app-check') {
      await signOutUnprovisionedAuth();
    }
    throw err;
  }

  return user;
}

export async function refreshAndMapUser(
  mapFirebaseUser: MapFirebaseUserFn,
  auth: Auth,
): Promise<AppUser | null> {
  if (!auth.currentUser) return null;
  await auth.currentUser.getIdToken(true);
  return mapFirebaseUser(auth.currentUser);
}

export function providerLabel(providerId: string): string {
  if (providerId === 'google.com') return 'Google';
  if (providerId === 'apple.com') return 'Apple';
  if (providerId === 'facebook.com') return 'Facebook';
  if (providerId === 'microsoft.com') return 'Microsoft';
  if (providerId === 'password') return 'Email';
  if (providerId === 'phone') return 'Phone';
  return providerId;
}

export function listProviders(user: User | null): Array<{ id: string; label: string }> {
  if (!user?.providerData?.length) return [];
  return user.providerData.map((p: UserInfo) => ({
    id: p.providerId,
    label: providerLabel(p.providerId),
  }));
}
